import { flow, makeObservable, observable } from 'mobx';
import RootStore from '.';
import BaseStore from './BaseStore';
import OrderApi from '@shared/apis/OrderApi';
import { ResponseData } from '@shared/types/http';
import { PurchaseOrder, OrderDetail } from '@shared/types/order';

export default class OrderStore extends BaseStore {
    orders: PurchaseOrder[] = [];
    totalOrders: number = 0;
    orderApi: OrderApi;

    constructor(rootStore: RootStore) {
        super(rootStore);
        makeObservable(this, {
            orders: observable,
            totalOrders: observable,
            fetchOrderHistory: flow.bound,
            getOrderDetail: flow.bound,
            exportInvoicePDF: flow.bound,
            rePayment: flow.bound
        });
        this.orderApi = new OrderApi();
    }

    *fetchOrderHistory(params?: any) {
        try {
            const res: any = yield this.rootStore.apiStore.call(
                this.orderApi,
                this.orderApi.getOrderHistory,
                params
            );

            // Handle wrapped response { ok, data: { elements, pagination } }
            if (res?.ok && res.data) {
                const { elements, pagination } = res.data;
                this.orders = elements || [];
                this.totalOrders = pagination?.totalRecord || this.orders.length;
                return res.data;
            }
        } catch (error) {
            console.error('Error fetchOrderHistory in OrderStore:', error);
        }
    }

    *getOrderDetail(orderId: string | number) {
        try {
            const res: ResponseData<OrderDetail> = yield this.rootStore.apiStore.call(
                this.orderApi,
                this.orderApi.getOrderDetail,
                orderId
            );

            if (res?.ok) {
                return res.data;
            }
            return null;
        } catch (error) {
            console.error('Error getOrderDetail in OrderStore:', error);
            return null;
        }
    }

    *exportInvoicePDF(orderId: string | number) {
        try {
            yield this.rootStore.apiStore.call(
                this.orderApi,
                this.orderApi.exportInvoicePDF,
                orderId
            );
            // ApiStore will handle the file download automatically
            return { success: true };
        } catch (error) {
            console.error('Error exportInvoicePDF in OrderStore:', error);
            return { success: false, error };
        }
    }

    /**
     * Get GMO payment URL for re-payment
     * @param orderId Order ID to re-pay
     */
    *rePayment(orderId: string | number) {
        try {
            const res: ResponseData<{ paymentUrl: string, orderCode: string }> = yield this.rootStore.apiStore.call(
                this.orderApi,
                () => this.orderApi.getRepaymentUrl(orderId)
            );

            if (res?.ok) {
                return res.data;
            }
        } catch (error: any) {
            throw error;
        }
    }

    clean() {
        super.clean();
        this.orders = [];
        this.totalOrders = 0;
    }
}
